// Pure calculation engine for the Zachary Group Builders Risk indication.
//
// Faithful port of the formulas in the source workbook
//   "2025-TZG Combined BR + Permanent Template - Updated.xlsx"
//
// See PRD.md §11.2 for the canonical formulas. No IO, no Date.now(), no
// side effects. Floats are carried through end-to-end; rounding is the UI's
// responsibility.
import { DEFAULT_RATE_CONFIG, lookupStateTaxRate } from './rates.js';
/**
 * Error thrown when calculation is requested for state="OTHER". Callers
 * should route to the contact form instead of attempting to quote.
 */
export class OtherStateError extends Error {
    name = 'OtherStateError';
    constructor(message = 'State is OTHER; calculation is not supported. Route to contact form.') {
        super(message);
    }
}
/**
 * Error thrown when a StateCode is priced but somehow missing from the
 * RateConfig's tax table. Distinct from OtherStateError so that a
 * misconfigured table surfaces rather than being silently bucketed as OTHER.
 */
export class MissingStateTaxError extends Error {
    name = 'MissingStateTaxError';
    constructor(state) {
        super(`No tax rate configured for state ${state}`);
    }
}
// ---------------------------------------------------------------------------
// Derived values
// ---------------------------------------------------------------------------
const MS_PER_DAY = 86_400_000;
/**
 * Parse an ISO date string (YYYY-MM-DD or full datetime) as UTC. Returns the
 * epoch-ms for midnight UTC on the given calendar date, so timezone drift
 * cannot affect projectDays.
 */
function parseDateUtc(iso) {
    // Accept "YYYY-MM-DD" and "YYYY-MM-DDTHH:MM:SS[.sss]Z/±HH:MM" forms.
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (dateOnly !== null) {
        const year = Number(dateOnly[1]);
        const month = Number(dateOnly[2]);
        const day = Number(dateOnly[3]);
        return Date.UTC(year, month - 1, day);
    }
    const ms = Date.parse(iso);
    if (Number.isNaN(ms)) {
        throw new Error(`Invalid ISO date: ${iso}`);
    }
    return ms;
}
export function computeDerived(input, config = DEFAULT_RATE_CONFIG) {
    if (input.state === 'OTHER') {
        throw new OtherStateError();
    }
    const lobi = input.lossOfBusinessIncome ?? 0;
    const tiv = input.hardCosts + input.softCosts + lobi;
    const startMs = parseDateUtc(input.startDate);
    const endMs = parseDateUtc(input.endDate);
    const projectDays = Math.round((endMs - startMs) / MS_PER_DAY);
    const proRata = projectDays / 365;
    const taxRate = lookupStateTaxRate(input.state, config);
    if (taxRate === null) {
        throw new MissingStateTaxError(input.state);
    }
    const constructionMultiplier = config.constructionMultipliers[input.constructionType];
    return { tiv, projectDays, proRata, taxRate, constructionMultiplier };
}
// ---------------------------------------------------------------------------
// Deductible credit lookup
// ---------------------------------------------------------------------------
function creditFor(tiers, deductible) {
    for (const tier of tiers) {
        if (tier.deductible === deductible)
            return tier.credit;
    }
    // Match the xlsx's VLOOKUP(..., FALSE) behavior: no match is an error.
    throw new Error(`No deductible credit tier for value ${deductible}`);
}
function resolveCredits(deductibles, factors) {
    return {
        brAop: creditFor(factors.brAop, deductibles.brAop),
        oiGl: creditFor(factors.oiGl, deductibles.oiGl),
        pollution: creditFor(factors.pollution, deductibles.pollution),
    };
}
// ---------------------------------------------------------------------------
// Scenario calculation
// ---------------------------------------------------------------------------
function rateFor(set, scenario) {
    return set[scenario];
}
function breakdownFromAnnual(annualPremium, proRata, triaRate, taxRate) {
    const termPremium = annualPremium * proRata;
    const tria = termPremium * triaRate;
    const taxesFees = termPremium * taxRate;
    return {
        annualPremium,
        termPremium,
        tria,
        taxesFees,
        total: termPremium + tria + taxesFees,
    };
}
/**
 * Pollution is computed term-first in the workbook (Builders Risk Indicator
 * I46:K46 combine TIV × rate/1000 × proRata × credit into the term line,
 * without a standalone annual line). We mirror that: annualPremium is left
 * 0 for shape uniformity.
 */
function pollutionBreakdown(termPremium, triaRate, taxRate) {
    const tria = termPremium * triaRate;
    const taxesFees = termPremium * taxRate;
    return {
        annualPremium: 0,
        termPremium,
        tria,
        taxesFees,
        total: termPremium + tria + taxesFees,
    };
}
export function computeScenario(input, scenario, derived, config = DEFAULT_RATE_CONFIG) {
    const { tiv, proRata, taxRate, constructionMultiplier } = derived;
    const rates = config.baseRates;
    const credits = resolveCredits(input.deductibles, config.deductibleCreditFactors);
    // Builders Risk — BR multiplier applied (construction multiplier). GL/
    // Excess/Pollution intentionally do NOT apply the multiplier per PRD §11.1.
    const brAnnual = tiv *
        rateFor(rates.builderRisk, scenario) *
        constructionMultiplier *
        credits.brAop;
    const builderRisk = breakdownFromAnnual(brAnnual, proRata, config.triaRate, taxRate);
    // OI General Liability — per $1,000 of TIV, deductible credit applied.
    const glAnnual = (tiv * rateFor(rates.oiGl, scenario) * credits.oiGl) / 1000;
    const oiGl = breakdownFromAnnual(glAnnual, proRata, config.triaRate, taxRate);
    // OI Excess $5M — per $1,000 of TIV. No deductible credit in the xlsx.
    const excessAnnual = (tiv * rateFor(rates.excess, scenario)) / 1000;
    const oiExcess = breakdownFromAnnual(excessAnnual, proRata, config.triaRate, taxRate);
    // Pollution — per $1,000 of TIV, term-first, deductible credit applied.
    const pollutionTerm = (tiv * rateFor(rates.pollution, scenario) * proRata * credits.pollution) / 1000;
    const pollution = pollutionBreakdown(pollutionTerm, config.triaRate, taxRate);
    const total = builderRisk.total + oiGl.total + oiExcess.total + pollution.total;
    const costPctOfCv = tiv === 0 ? 0 : total / tiv;
    return {
        scenario,
        builderRisk,
        oiGl,
        oiExcess,
        pollution,
        total,
        costPctOfCv,
    };
}
// ---------------------------------------------------------------------------
// Permanent Insurance
// ---------------------------------------------------------------------------
export function computePermanent(input, mode, derived, config = DEFAULT_RATE_CONFIG) {
    const { taxRate } = derived;
    const triaRate = config.triaRate;
    // Property: hardCosts (not TIV) per $100. The Permanent sheet references
    // Builders Risk Indicator!C17 (hard costs) as Building Value, with Contents
    // and LOBI held at 0 in the sheet itself.
    const propertyAnnual = (input.hardCosts * config.baseRates.permanentProperty[mode]) / 100;
    const propertyTria = propertyAnnual * triaRate;
    const propertyTax = propertyAnnual * taxRate;
    const property = {
        annualPremium: propertyAnnual,
        termPremium: propertyAnnual,
        tria: propertyTria,
        taxesFees: propertyTax,
        total: propertyAnnual + propertyTria + propertyTax,
    };
    // GL: per-unit flat annual. TRIA and taxes both computed against annual.
    const glAnnual = input.numberOfUnits * config.baseRates.permanentGl[mode];
    const glTria = glAnnual * triaRate;
    const glTax = glAnnual * taxRate;
    const gl = {
        annualPremium: glAnnual,
        termPremium: glAnnual,
        tria: glTria,
        taxesFees: glTax,
        total: glAnnual + glTria + glTax,
    };
    return {
        mode,
        property,
        gl,
        total: property.total + gl.total,
    };
}
// ---------------------------------------------------------------------------
// Orchestrator
// ---------------------------------------------------------------------------
function variance(from, to) {
    const delta = to - from;
    const pct = from === 0 ? 0 : delta / from;
    return { delta, pct };
}
export function computeIndication(input, config = DEFAULT_RATE_CONFIG) {
    if (input.state === 'OTHER') {
        throw new OtherStateError();
    }
    const derived = computeDerived(input, config);
    const best = computeScenario(input, 'best', derived, config);
    const average = computeScenario(input, 'average', derived, config);
    const conservative = computeScenario(input, 'conservative', derived, config);
    const permAggressive = computePermanent(input, 'aggressive', derived, config);
    const permConservative = computePermanent(input, 'conservative', derived, config);
    // Variance formulas follow the xlsx (Builders Risk Indicator!C39:E40):
    //   Best vs Avg    delta = D34-C34 (avg-best), pct = /C34 (best)
    //   Avg vs Cons    delta = E34-D34 (cons-avg), pct = /D34 (avg)
    //   Best vs Cons   delta = E34-C34 (cons-best), pct = /C34 (best)
    return {
        tiv: derived.tiv,
        projectDays: derived.projectDays,
        proRata: derived.proRata,
        taxRate: derived.taxRate,
        constructionMultiplier: derived.constructionMultiplier,
        scenarios: { best, average, conservative },
        permanent: {
            aggressive: permAggressive,
            conservative: permConservative,
        },
        variance: {
            bestToAverage: variance(best.total, average.total),
            averageToConservative: variance(average.total, conservative.total),
            bestToConservative: variance(best.total, conservative.total),
        },
        rateConfigVersion: config.configVersion,
    };
}
// Re-export helpers callers commonly need alongside the orchestrator.
export { DEFAULT_RATE_CONFIG, lookupStateTaxRate };
//# sourceMappingURL=calc.js.map